/**
 * 🎬 JobRequestActions Component
 * Fecha: 30 de octubre de 2025
 * 
 * Componente que muestra los botones de acción apropiados según:
 * - El estado actual de la solicitud
 * - El rol del usuario
 * - Los permisos del usuario
 */

import React, { useState } from 'react'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { CheckCircle, DollarSign, CheckCircle2, CornerUpLeft, RotateCcw, XCircle } from 'lucide-react'
import type { JobRequest } from '@/services/api'
import { useAuth } from '@/shared/hooks/useAuth'
import {
  canAssignJobRequestSalaries,
  canFinalApproveJobRequests,
  canPreApproveJobRequests,
  canRejectJobRequests,
} from '@/shared/lib/roleUtils'
import { ActionModal } from '@/shared/components/ActionModal'
import PreApproveModal from '@/features/rh/components/job-requests/modals/PreApproveModal'
import AssignSalariesModal from '@/features/rh/components/job-requests/modals/AssignSalariesModal'
import FinalApproveModal from '@/features/rh/components/job-requests/modals/FinalApproveModal'
import ReturnToRHModal from '@/features/rh/components/job-requests/modals/ReturnToRHModal'

interface JobRequestActionsProps {
  jobRequest: JobRequest
  onUpdate: (updated?: JobRequest) => void
  className?: string
}

const JobRequestActions: React.FC<JobRequestActionsProps> = ({
  jobRequest,
  onUpdate,
  className = ''
}) => {
  const { user } = useAuth()
  const [showPreApproveModal, setShowPreApproveModal] = useState(false)
  const [showAssignSalariesModal, setShowAssignSalariesModal] = useState(false)
  const [showFinalApproveModal, setShowFinalApproveModal] = useState(false)
  const [showReturnToRHModal, setShowReturnToRHModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showReactivateModal, setShowReactivateModal] = useState(false)

  const canPreApprove = canPreApproveJobRequests(user || undefined)
  const canAssignSalaries = canAssignJobRequestSalaries(user || undefined)
  const canFinalApprove = canFinalApproveJobRequests(user || undefined)
  const canReject = canRejectJobRequests(user || undefined)
  const canReturnToRH = Boolean(
    user &&
    jobRequest?.solicitante &&
    (user.id === jobRequest.solicitante.id ||
      user.email === jobRequest.solicitante.email)
  )

  const handleSuccess = (updated: JobRequest) => {
    onUpdate(updated)
  }

  // No mostrar nada si no hay acciones disponibles
  /**
   * Reactivar lo puede quien podia rechazar: si pudo frenarla, puede
   * deshacerlo (SRH-317). Una solicitud cubierta no se ofrece —se cerro
   * porque la plaza se ocupo— y para ese caso esta duplicar.
   */
  const puedeReactivar = canReject && jobRequest.status === 'rechazada'

  if (
    !canPreApprove &&
    !canAssignSalaries &&
    !canFinalApprove &&
    !canReject &&
    !canReturnToRH &&
    !puedeReactivar
  ) {
    return null
  }

  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {/* Botón: Pre-aprobar (PM, estado: pendiente_validacion_pm) */}
      {(jobRequest.status === 'pendiente_pre_aprobacion' ||
        jobRequest.status === 'pendiente_validacion_pm') && (
        <>
          {canPreApprove && (
            <button
              onClick={() => setShowPreApproveModal(true)}
              className={estiloDeBoton('primario')}
            >
              <CheckCircle className="w-4 h-4" />
              <span>Pre-aprobar</span>
            </button>
          )}
          {/* Rechazar junto a Pre-aprobar. Lo habilita el permiso «Eliminar
              solicitudes», no el de aprobar: son cosas distintas. */}
          {canReject && (
            <button
              onClick={() => setShowRejectModal(true)}
              className={estiloDeBoton('peligro')}
            >
              <XCircle className="w-4 h-4" />
              <span>Rechazar</span>
            </button>
          )}
        </>
      )}

      {canAssignSalaries && (
        jobRequest.status === 'pre_aprobada' ||
        jobRequest.status === 'devuelta_rh' ||
        jobRequest.status === 'en_revision'
      ) && (
        <button
          onClick={() => setShowAssignSalariesModal(true)}
          className={estiloDeBoton('primario')}
        >
          <DollarSign className="w-4 h-4" />
          <span>{jobRequest.status === 'devuelta_rh' ? 'Ajustar salarios' : 'Asignar salarios'}</span>
        </button>
      )}

      {(jobRequest.status === 'pendiente_aprobacion_salario' ||
        jobRequest.status === 'en_revision_salarios') && (
        <>
          {canFinalApprove && (
            <button
              onClick={() => setShowFinalApproveModal(true)}
              className={estiloDeBoton('primario')}
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Aprobar propuesta salarial</span>
            </button>
          )}
          {canReject && (
            <button
              onClick={() => setShowRejectModal(true)}
              className={estiloDeBoton('peligro')}
            >
              <XCircle className="w-4 h-4" />
              <span>Rechazar</span>
            </button>
          )}

          {canReturnToRH && (
            <button
              onClick={() => setShowReturnToRHModal(true)}
              className={estiloDeBoton('secundario')}
            >
              <CornerUpLeft className="w-4 h-4" />
              <span>Devolver a RH</span>
            </button>
          )}
        </>
      )}

      {/* Modales */}
      {showPreApproveModal && (
        <PreApproveModal
          jobRequest={jobRequest}
          isOpen={showPreApproveModal}
          onClose={() => setShowPreApproveModal(false)}
          onSuccess={handleSuccess}
        />
      )}

      {showAssignSalariesModal && (
        <AssignSalariesModal
          jobRequest={jobRequest}
          isOpen={showAssignSalariesModal}
          onClose={() => setShowAssignSalariesModal(false)}
          onSuccess={handleSuccess}
        />
      )}

      {showFinalApproveModal && (
        <FinalApproveModal
          jobRequest={jobRequest}
          isOpen={showFinalApproveModal}
          onClose={() => setShowFinalApproveModal(false)}
          onSuccess={handleSuccess}
        />
      )}

      {showReturnToRHModal && (
        <ReturnToRHModal
          jobRequest={jobRequest}
          isOpen={showReturnToRHModal}
          onClose={() => setShowReturnToRHModal(false)}
          onSuccess={handleSuccess}
        />
      )}
      {puedeReactivar && (
        <button
          onClick={() => setShowReactivateModal(true)}
          className={estiloDeBoton('primario')}
        >
          <RotateCcw className="w-4 h-4" />
          <span>Reactivar</span>
        </button>
      )}

      {showReactivateModal && (
        <ActionModal
          isOpen={showReactivateModal}
          action="reactivate"
          requestId={jobRequest.id.toString()}
          onSuccess={() => {
            setShowReactivateModal(false)
            onUpdate()
          }}
          onCancel={() => setShowReactivateModal(false)}
        />
      )}

      {showRejectModal && (
        <ActionModal
          isOpen={showRejectModal}
          action="reject"
          requestId={jobRequest.id.toString()}
          onSuccess={() => {
            setShowRejectModal(false)
            onUpdate()
          }}
          onCancel={() => setShowRejectModal(false)}
        />
      )}
    </div>
  )
}

export default JobRequestActions
